import { useEffect, useState } from "react";
import { getCustomNews } from "../../actions/ApiCalls";
import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import { useNavigate } from "react-router-dom";
import CustomBodyComponent from "./CustomBodyComponent";
import CustomImageComponent from "./CustomImageComponent";
import CustomTableComponent from "./CustomTableComponent";
import CustomImageTextComponent from "./CustomImageTextComponent";
import Custom4ImageGalleryComponent from "./Custom4ImageGallery";
import CustomGalleryComponent from "./CustomGalleryComponent";
import CustomQuote from "./CustomQuote";

interface NewsStoryProps {
    slug: string;
}

interface ComponentData {
    id: number;
    type: string;
    display_order: number;
    data: any;
}

interface ArticleData {
    id: number;
    title: string;
    subtitle: string;
    slug: string;
    cover_image: string;
    draft: boolean;
    date_posted: string;
    components: ComponentData[];
}

function CustomNewsStory({ slug }: NewsStoryProps) {
    const [article, setArticle] = useState<ArticleData | null>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const navigate = useNavigate();

    useEffect(() => {
        fetchComponents();
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    function fetchComponents() {
        getCustomNews(slug)
            .then((response) => {
                const data = { ...response.data };
                setArticle(data);
                document.title = "Vinimark | " + data.title;
            })
            .catch((error) => {
                console.error("Failed to fetch components:", error);
            });
    }

    if (!article) {
        return <div>Loading...</div>;
    }

    const linkStyle: React.CSSProperties = {
        color: 'black',
        fontSize: '10px',
        fontFamily: "'Vinimark-Diatype', sans-serif",
        fontWeight: 600,
        textTransform: 'uppercase',
        textDecoration: 'underline',
        cursor: 'pointer',
    };

    const textTypes = ["body", "imageText", "textImage", "quote", "textQuote"];
    const firstTextIndex = article.components.findIndex(c =>
        textTypes.includes(c.type)
    );

    const showTopHeader = firstTextIndex > 1;

    function getComponent(component: ComponentData, index: number) {
        const includeHeader = index === firstTextIndex && firstTextIndex <= 1;

        switch (component.type) {
            case "body":
                return (
                    <CustomBodyComponent
                        id={component.data.id}
                        body_copy={component.data.body_copy}
                        title={includeHeader ? article?.title : undefined}
                        date_posted={includeHeader ? article?.date_posted : undefined}
                    />
                );
            case "image":
                return (
                    <CustomImageComponent
                        id={component.data.id}
                        caption={component.data.caption}
                        isFullWidth={component.data.is_full_width}
                        image_path={component.data.image_path}
                    />
                );
            case "table":
                return (
                    <CustomTableComponent
                        id={component.data.id}
                        number_columns={component.data.number_columns}
                        content1={component.data.content1}
                        content2={component.data.content2}
                        content3={component.data.content3}
                        content4={component.data.content4}
                    />
                );
            case "imageText":
            case "textImage":
                return (
                    <CustomImageTextComponent
                        id={component.data.id}
                        body_copy={component.data.body_copy}
                        image_path={component.data.image_path}
                        image_first={component.data.image_first}
                        title={includeHeader ? article?.title : undefined}
                        date_posted={includeHeader ? article?.date_posted : undefined}
                    />
                );
            case "quote":
            case "textQuote":
                return (
                    <CustomQuote
                        id={component.data.id}
                        quote={component.data.quote}
                        body_copy={component.data.body_copy}
                        quote_first={component.data.quote_first}
                        title={includeHeader ? article?.title : undefined}
                        date_posted={includeHeader ? article?.date_posted : undefined}
                    />
                );
            case "gallery4":
                return (
                    <Custom4ImageGalleryComponent
                        id={component.data.id}
                        image_1={component.data.image1_path}
                        image_2={component.data.image2_path}
                        image_3={component.data.image3_path}
                        image_4={component.data.image4_path}
                    />
                );
            case "gallery":
                return <CustomGalleryComponent data={component.data} />;
            default:
                return <div>Unknown component type</div>;
        }
    }

    return (
        <div>
            <Header />

            {showTopHeader && (
                <div style={{ marginTop: "50px", marginLeft: "26px" }}>
                    <h1>{article.title}</h1>
                    <p>{article.date_posted}</p>
                </div>
            )}

            <div
                style={{
                    display: "flex",
                    flexDirection: windowWidth > 835 ? "row" : "column",
                    marginTop: "140px",
                }}
            >
                <div style={{ flex: 1 }} />
                <div style={{ flex: 1, textAlign: "left" }}>
                    <p style={{ fontWeight: 400, marginLeft: "26px" }}>
                        <a style={linkStyle} onClick={() => navigate("/news")}>
                            BACK to stories
                        </a>
                    </p>
                </div>
            </div>

            <div>
                {article.components.map((comp, i) => (
                    <div key={comp.id} style={{ marginTop: "100px" }}>
                        {getComponent(comp, i)}
                    </div>
                ))}
            </div>

            <Footer />
        </div>
    );
}




export default CustomNewsStory;
